"use client";

import { useMemo, useRef } from "react";
import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { cn } from "@workspace/ui/lib/utils";
import "@/features/home/components/landing-v2/scroll-reveal.css";

gsap.registerPlugin(ScrollTrigger, useGSAP);

/**
 * @typedef {Object} ScrollRevealProps
 * @property {import("react").ReactNode} children 要揭示的文本或节点；字符串会按词拆分。
 * @property {import("react").RefObject<HTMLElement | null>} [scrollContainerRef]
 * 可选滚动容器；默认使用 window。
 * @property {boolean} [active] 外部激活状态；提供时由父级滚动状态驱动。
 * @property {boolean} [enableBlur] 是否启用逐词模糊消散。
 * @property {number} [baseOpacity] 单词揭示前的透明度。
 * @property {number} [baseRotation] 容器揭示前的旋转角度。
 * @property {number} [blurStrength] 单词揭示前的模糊半径。
 * @property {string} [className] 容器附加类名。
 * @property {string} [containerClassName] 与 React Bits API 对齐的容器附加类名。
 * @property {string} [textClassName] 文本段落附加类名。
 * @property {string} [wordClassName] 每个单词附加类名。
 * @property {string} [rotationEnd] 独立 ScrollTrigger 的旋转结束位置。
 * @property {string} [wordAnimationEnd] 独立 ScrollTrigger 的逐词动画结束位置。
 */

/**
 * React Bits ScrollReveal 的页面级适配器。
 * `active` 存在时由外部滚动状态驱动；省略时创建独立 ScrollTrigger。
 * @param {ScrollRevealProps} props
 */
export default function ScrollReveal({
  children,
  scrollContainerRef,
  active,
  enableBlur = true,
  baseOpacity = 0.1,
  baseRotation = 3,
  blurStrength = 4,
  className = "",
  containerClassName = "",
  textClassName = "",
  wordClassName = "",
  rotationEnd = "bottom bottom",
  wordAnimationEnd = "bottom bottom",
}) {
  const containerRef = useRef(null);
  const hiddenFilter = enableBlur ? `blur(${blurStrength}px)` : "blur(0px)";
  const splitText = useMemo(() => {
    if (typeof children !== "string") return children;
    const initialWordStyle = typeof active === "boolean"
      ? {
          opacity: baseOpacity,
          ...(enableBlur ? { filter: hiddenFilter } : {}),
        }
      : undefined;

    return children.split(/(\s+)/).map((segment, index) => {
      if (/^\s+$/.test(segment)) return segment;

      return (
        <span
          className={cn("scroll-reveal__word", wordClassName)}
          key={`${segment}-${index}`}
          style={initialWordStyle}
        >
          {segment}
        </span>
      );
    });
  }, [active, baseOpacity, children, enableBlur, hiddenFilter, wordClassName]);

  useGSAP(
    () => {
      const container = containerRef.current;
      if (!container) return undefined;

      const words = container.querySelectorAll(".scroll-reveal__word");
      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const setWordState = (visible) => {
        if (!words.length) return;
        gsap.set(words, {
          opacity: visible ? 1 : baseOpacity,
          ...(enableBlur ? { filter: visible ? "blur(0px)" : hiddenFilter } : {}),
        });
      };

      if (reduceMotion) {
        gsap.set(container, { rotate: 0 });
        setWordState(active !== false);
        return undefined;
      }

      if (typeof active === "boolean") {
        if (!active) {
          gsap.set(container, { rotate: baseRotation, transformOrigin: "0% 50%" });
          setWordState(false);
          return undefined;
        }

        const timeline = gsap.timeline();
        timeline.fromTo(
          container,
          { rotate: baseRotation, transformOrigin: "0% 50%" },
          { rotate: 0, duration: 0.75, ease: "power3.out", overwrite: "auto" },
          0,
        );
        if (words.length) {
          timeline.fromTo(
            words,
            {
              opacity: baseOpacity,
              ...(enableBlur ? { filter: hiddenFilter } : {}),
              willChange: enableBlur ? "opacity, filter" : "opacity",
            },
            {
              opacity: 1,
              ...(enableBlur ? { filter: "blur(0px)" } : {}),
              duration: 0.75,
              ease: "power3.out",
              stagger: 0.05,
              overwrite: "auto",
              onComplete: () => gsap.set(words, { clearProps: "willChange" }),
            },
            0,
          );
        }
        return undefined;
      }

      const scroller = scrollContainerRef?.current || window;
      gsap.fromTo(
        container,
        { rotate: baseRotation, transformOrigin: "0% 50%" },
        {
          rotate: 0,
          ease: "none",
          scrollTrigger: {
            trigger: container,
            scroller,
            start: "top bottom",
            end: rotationEnd,
            scrub: true,
          },
        },
      );

      if (words.length) {
        gsap.fromTo(
          words,
          {
            opacity: baseOpacity,
            ...(enableBlur ? { filter: hiddenFilter } : {}),
            willChange: enableBlur ? "opacity, filter" : "opacity",
          },
          {
            opacity: 1,
            ...(enableBlur ? { filter: "blur(0px)" } : {}),
            ease: "none",
            stagger: 0.05,
            scrollTrigger: {
              trigger: container,
              scroller,
              start: "top bottom-=20%",
              end: wordAnimationEnd,
              scrub: true,
            },
          },
        );
      }

      return undefined;
    },
    {
      scope: containerRef,
      dependencies: [
        active,
        baseOpacity,
        baseRotation,
        blurStrength,
        children,
        enableBlur,
        rotationEnd,
        scrollContainerRef,
        wordAnimationEnd,
      ],
      revertOnUpdate: true,
    },
  );

  return (
    <div
      ref={containerRef}
      className={cn("scroll-reveal", className, containerClassName)}
      data-scroll-reveal-active={typeof active === "boolean" ? String(active) : undefined}
      style={typeof active === "boolean" ? { transform: `rotate(${baseRotation}deg)` } : undefined}
    >
      <p className={cn("scroll-reveal__text", textClassName)}>{splitText}</p>
    </div>
  );
}
